import { useChatApi } from "@ui/agent/components/agent/api/chat-api";
import { useCallback, useEffect, useRef } from "react";

import { useChatsContext } from "@/lib/context/ChatsProvider/hooks/useChatsContext";
import { useAxios } from "@/lib/hooks";

export const useMarkThreadAsRead = (threadId?: string) => {
  const { axiosInstance } = useAxios();
  const { markThreadAsRead } = useChatApi(axiosInstance);
  const { allChats, setAllChats } = useChatsContext();

  const allChatsRef = useRef(allChats);
  allChatsRef.current = allChats;

  const markThreadAsReadRef = useRef(markThreadAsRead);
  markThreadAsReadRef.current = markThreadAsRead;

  const inFlightRef = useRef<Set<string>>(new Set());

  const markAsRead = useCallback(
    async (id: string, { force = false }: { force?: boolean } = {}) => {
      if (inFlightRef.current.has(id)) return;

      const isAlreadyRead = allChatsRef.current.some((chat) => {
        if (chat.chat_id !== id) return false;
        if (!chat.last_message_at) return true;
        return chat.last_read_at && chat.last_read_at >= chat.last_message_at;
      });

      if (isAlreadyRead && !force) return;

      const lastReadAt = new Date().toISOString();

      inFlightRef.current.add(id);
      try {
        await markThreadAsReadRef.current(id);
        setAllChats((chats) =>
          chats.map((chat) =>
            chat.chat_id === id ? { ...chat, last_read_at: lastReadAt } : chat,
          ),
        );
      } finally {
        inFlightRef.current.delete(id);
      }
    },
    [setAllChats],
  );

  useEffect(() => {
    if (threadId) {
      void markAsRead(threadId);
    }
  }, [threadId, markAsRead]);

  return markAsRead;
};
